
import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useState,
} from "react";
import { Chat, Message } from "../types/chat.types";

// Chat State
interface ChatState {
  chats: Chat[];
  selectedChatId: string | null;
  members: string[];
  messages: Record<string, Message[]>;
  searchQuery: string;
  loading: {
    chats: boolean;
    messages: boolean;
    sending: boolean;
  };
  error: {
    chats: string | null;
    messages: string | null;
    sending: string | null;
  };
}

// Chat Actions
type ChatAction =
  | { type: "SET_CHATS"; payload: Chat[] }
  | { type: "UPDATE_CHAT"; payload: Partial<Chat> & { chatId: string } }
  | { type: "SET_CHATS_LOADING"; payload: boolean }
  | { type: "SET_CHATS_ERROR"; payload: string | null }
  | { type: "SELECT_CHAT"; payload: string | null }
  | { type: "SET_MESSAGES"; payload: { chatId: string; messages: Message[] } }
  | { type: "MERGE_MESSAGES"; payload: { chatId: string; messages: Message[] } }
  | { type: "SET_MESSAGES_LOADING"; payload: boolean }
  | { type: "SET_MESSAGES_ERROR"; payload: string | null }
  | { type: "ADD_MESSAGE"; payload: { chatId: string; message: Message } }
  | { type: "SET_SEARCH_QUERY"; payload: string }
  | { type: "SET_SENDING"; payload: boolean }
  | { type: "SET_SENDING_ERROR"; payload: string | null }
  | { type: "REMOVE_CHAT_FROM_STATE"; payload: string }
  | { type: "RESET_STATE" }
  | { type: "REMOVE_CHAT"; payload: string };

// Initial State
const initialState: ChatState = {
  chats: [],
  selectedChatId: null,
  messages: {},
  members: [],
  searchQuery: "",
  loading: {
    chats: false,
    messages: false,
    sending: false,
  },
  error: {
    chats: null,
    messages: null,
    sending: null,
  },
};

// Reducer
const chatReducer = (state: ChatState, action: ChatAction): ChatState => {
  switch (action.type) {
    case "SET_CHATS":
      console.warn(action.payload, "action.payload")
      return {
        ...state,
        chats: action.payload,
        loading: { ...state.loading, chats: false },
        error: { ...state.error, chats: null },
      };

    case "UPDATE_CHAT": {
      const existingChatIndex = state.chats.findIndex(
        (chat) => chat.chatId === action.payload.chatId
      );

      if (existingChatIndex !== -1) {
        // Update existing chat
        return {
          ...state,
          chats: state.chats.map((chat, index) =>
            index === existingChatIndex ? { ...chat, ...action.payload } : chat
          ),
        };
      } else {
        // Add new chat
        return {
          ...state,
          chats: [ action.payload as Chat, ...state.chats],
        };
      }
    }
    case "SET_CHATS_LOADING":
      return {
        ...state,
        loading: { ...state.loading, chats: action.payload },
      };

    case "SET_CHATS_ERROR":
      return {
        ...state,
        error: { ...state.error, chats: action.payload },
        loading: { ...state.loading, chats: false },
      };

    case "SELECT_CHAT":
      return {
        ...state,
        selectedChatId: action.payload,
      };

    case "SET_MESSAGES":
      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.chatId]: action.payload.messages,
        },
        loading: { ...state.loading, messages: false },
        error: { ...state.error, messages: null },
      };

    case "MERGE_MESSAGES": {
      const { chatId, messages: newMessages } = action.payload;
      const existing = state.messages[chatId] || [];

      const merged = Array.from(
        new Map([...existing, ...newMessages].map((m) => [m.id, m])).values()
      );

      return {
        ...state,
        messages: {
          ...state.messages,
          [chatId]: merged,
        },
      };
    }

    case "SET_MESSAGES_LOADING":
      return {
        ...state,
        loading: { ...state.loading, messages: action.payload },
      };

    case "SET_MESSAGES_ERROR":
      return {
        ...state,
        error: { ...state.error, messages: action.payload },
        loading: { ...state.loading, messages: false },
      };

    case "ADD_MESSAGE": {
      const { chatId, message } = action.payload;
      const prev = state.messages[chatId] || [];
      return {
        ...state,
        messages: {
          ...state.messages,
          [chatId]: [...prev, message],
        },
      };
    }

    case "SET_SEARCH_QUERY":
      return {
        ...state,
        searchQuery: action.payload,
      };

    case "SET_SENDING":
      return {
        ...state,
        loading: { ...state.loading, sending: action.payload },
      };

    case "SET_SENDING_ERROR":
      return {
        ...state,
        error: { ...state.error, sending: action.payload },
        loading: { ...state.loading, sending: false },
      };

    case "REMOVE_CHAT_FROM_STATE":
      return {
        ...state,
        chats: state.chats.filter((chat) => chat.id !== action.payload),
      };
    case "RESET_STATE":
      return initialState;

    default:
      return state;
  }
};

// Context
interface ChatContextType {
  state: ChatState;
  dispatch: React.Dispatch<ChatAction>;
  actions: {
    setChats: (chats: Chat[]) => void;
    updateChat: (chatId: string, data: Partial<Chat>) => void;
    selectChat: (chatId: string | null) => void;
    setSearchQuery: (query: string) => void;
    addMessage: (chatId: string, message: Message) => void;
    setMessages: (chatId: string, messages: Message[]) => void;
    mergeMessages: (chatId: string, messages: Message[]) => void;
    resetState: () => void;
  };
  setChatDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  chatDrawer: boolean;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

// Provider
interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const [chatDrawer, setChatDrawer] = useState(false);

  const actions = {
    setChats: (chats: Chat[]) => {
      dispatch({ type: "SET_CHATS", payload: chats });
    },
    updateChat: (chatId: string, data: Partial<Chat>) => {
      dispatch({ type: "UPDATE_CHAT", payload: { chatId, ...data } });
    },
    selectChat: (chatId: string | null) => {
      dispatch({ type: "SELECT_CHAT", payload: chatId });
    },
    setSearchQuery: (query: string) => {
      dispatch({ type: "SET_SEARCH_QUERY", payload: query });
    },
    addMessage: (chatId: string, message: Message) => {
      dispatch({ type: "ADD_MESSAGE", payload: { chatId, message } });
    },
    setMessages: (chatId: string, messages: Message[]) => {
      dispatch({ type: "SET_MESSAGES", payload: { chatId, messages } });
    },
    mergeMessages: (chatId: string, messages: Message[]) => {
      dispatch({ type: "MERGE_MESSAGES", payload: { chatId, messages } });
    },
    resetState: () => {
      dispatch({ type: "RESET_STATE" });
    },
  };

  return (
    <ChatContext.Provider
      value={{ state, dispatch, actions, setChatDrawer, chatDrawer }}
    >
      {children}
    </ChatContext.Provider>
  );
};

// Hook to use Chat Context
export const useChatContext = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChatContext must be used within a ChatProvider");
  }
  return context;
};
